import { type JSX, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import { PaperFormat } from '../../api/gql/graphql';
import { Header } from '../../components/accessible-components';
import { ImageNames, useImageSrc } from '../../services/asset-src-controller';
import { useScannerStore, useSessionStore } from '../../store';
import { SoloOnboardingStage } from '../../types/types';
import { ScannerVariant } from '../scanner/utils';
import { OnboardingImagePage } from './components/onboarding-image-page';
import { OnboardingPaperSizeSelect } from './components/onboarding-paper-size-select';
import { OnboardingStartPage } from './components/onboarding-start-page';

interface SoloOnboardingProps {
  onClose: () => void;
  onBack: () => void;
  onNext: () => void;
}

export function SoloOnboarding({
  onBack,
  onClose,
  onNext,
}: SoloOnboardingProps): JSX.Element {
  const { t } = useTranslation();
  const { paperType } = useSessionStore((store) => store);
  const { setPaperFormat } = useScannerStore((store) => store);
  const [soloOnboardingView, setSoloOnboardingView] = useState(
    SoloOnboardingStage.RemoveShoes,
  );

  const rollUpPants = t('onboarding.rollPants');
  const removeShoes = t('onboarding.removeShoes');
  const standAsShown = <Trans>{t('onboarding.standAsShown')}</Trans>;

  const onBackClick = (): void => {
    switch (soloOnboardingView) {
      case SoloOnboardingStage.RemoveShoes: {
        onBack();
        break;
      }
      case SoloOnboardingStage.RollPants: {
        setSoloOnboardingView(SoloOnboardingStage.RemoveShoes);
        break;
      }
      case SoloOnboardingStage.PaperSizeSelect: {
        setSoloOnboardingView(SoloOnboardingStage.RollPants);
        break;
      }
      case SoloOnboardingStage.NextTakeShots: {
        setSoloOnboardingView(
          paperType
            ? SoloOnboardingStage.RollPants
            : SoloOnboardingStage.PaperSizeSelect,
        );
        break;
      }
      case SoloOnboardingStage.StandAsShown: {
        setSoloOnboardingView(SoloOnboardingStage.NextTakeShots);
        break;
      }
      default: {
        throw new Error('Invalid onboarding stage');
      }
    }
  };

  const getImage = useImageSrc(ScannerVariant.Solo);
  const child = useMemo(() => {
    switch (soloOnboardingView) {
      case SoloOnboardingStage.RemoveShoes: {
        return (
          <OnboardingImagePage
            instruction={removeShoes}
            imageSrc={getImage(ImageNames.TakeOfShoesImage)}
            imageNumber={1}
            imageCount={3}
            buttonText={t('button.next')}
            altText={t('imageAlt.onboardig.removeShoes')}
            onClick={() => {
              setSoloOnboardingView(SoloOnboardingStage.RollPants);
            }}
          />
        );
      }
      case SoloOnboardingStage.RollPants: {
        return (
          <OnboardingImagePage
            instruction={rollUpPants}
            imageSrc={getImage(ImageNames.RollUpPantsImage)}
            imageNumber={2}
            imageCount={3}
            buttonText={t('button.next')}
            altText={t('imageAlt.onboardig.rollPants')}
            onClick={() => {
              switch (paperType) {
                case 'a4': {
                  setPaperFormat(PaperFormat.A4);
                  setSoloOnboardingView(SoloOnboardingStage.NextTakeShots);
                  break;
                }
                case 'us-letter': {
                  setPaperFormat(PaperFormat.Letter);
                  setSoloOnboardingView(SoloOnboardingStage.NextTakeShots);
                  break;
                }
                default: {
                  setSoloOnboardingView(SoloOnboardingStage.PaperSizeSelect);
                  break;
                }
              }
            }}
          />
        );
      }
      case SoloOnboardingStage.PaperSizeSelect: {
        return (
          <OnboardingPaperSizeSelect
            onClick={() => {
              setSoloOnboardingView(SoloOnboardingStage.NextTakeShots);
              usageTracker.track(UsageTrackerEvent.OnboardingScannerDetail, {
                scanner_type: 'solo',
              });
            }}
            onClose={onClose}
            imageSrc={getImage(ImageNames.PlacePaperImage)}
            buttonText={t('button.next')}
            imageNumber={3}
            imageCount={3}
            altText={t('imageAlt.onboardig.paperPlacement')}
          />
        );
      }
      case SoloOnboardingStage.NextTakeShots: {
        return (
          <OnboardingStartPage
            onClick={() => {
              setSoloOnboardingView(SoloOnboardingStage.StandAsShown);
            }}
            imageSrc={getImage(ImageNames.LandingPageImage)}
            buttonText={t('button.gotIt')}
            text={t('onboarding.takeTwoShots')}
            description={t('onboarding.twoShotsDescription')}
            altText={t('imageAlt.onboardig.twoShots')}
          />
        );
      }
      case SoloOnboardingStage.StandAsShown: {
        return (
          <OnboardingImagePage
            instruction={standAsShown}
            imageSrc={getImage(ImageNames.PoseImage1)}
            buttonText={t('button.ready')}
            altText={t('imageAlt.onboardig.standAsShown')}
            onClick={onNext}
          />
        );
      }
      default: {
        throw new Error('Invalid onboarding stage');
      }
    }
  }, [soloOnboardingView, onClose]);
  return (
    <>
      <Header back={onBackClick} close={onClose} />
      {child}
    </>
  );
}
